import {
  signInWithRedirect as amplifySignInWithRedirect,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  confirmSignIn as amplifyConfirmSignIn,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  autoSignIn as amplifyAutoSignIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  associateWebAuthnCredential as amplifyAssociateWebAuthnCredential,
  listWebAuthnCredentials as amplifyListWebAuthnCredentials,
  deleteWebAuthnCredential as amplifyDeleteWebAuthnCredential,
  rememberDevice as amplifyRememberDevice,
  forgetDevice as amplifyForgetDevice,
  fetchDevices as amplifyFetchDevices,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResendSignUpCodeInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
  type SignInWithRedirectInput,
  type ForgetDeviceInput,
  type FetchDevicesOutput,
} from "aws-amplify/auth"

/**
 * Social authentication provider types
 */
export type OAuthProvider = "Google" | "Facebook" | "Apple" | string

/**
 * Options for loginWithRedirect
 */
export interface LoginWithRedirectOptions {
  provider?: OAuthProvider
}

/**
 * Redirect user to Cognito OAuth provider for login
 * @param options - Options including provider selection
 */
export const loginWithRedirect = async (options?: LoginWithRedirectOptions) => {
  const config: SignInWithRedirectInput = {}

  if (options?.provider) {
    // Cast to Amplify's provider type which allows string
    config.provider = options.provider as SignInWithRedirectInput["provider"]
  }

  await amplifySignInWithRedirect(config)
}

/**
 * Sign in with email and password
 * @param email - User's email address
 * @param password - User's password
 */
export const signInWithEmail = async (email: string, password: string) => {
  const input: SignInInput = {
    username: email,
    password,
  }
  const output = await amplifySignIn(input)
  return output
}

/**
 * Sign up with email and password
 * @param email - User's email address
 * @param password - User's password
 * @param attributes - Additional user attributes (name, etc.)
 */
export const signUpWithEmail = async (
  email: string,
  password: string,
  attributes?: Record<string, string>
) => {
  const input: SignUpInput = {
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        ...attributes,
      },
      autoSignIn: true,
    },
  }
  const output = await amplifySignUp(input)
  return output
}

/**
 * Confirm sign up with verification code
 * @param email - User's email address
 * @param code - Verification code from email
 */
export const confirmSignUpCode = async (email: string, code: string) => {
  const input: ConfirmSignUpInput = {
    username: email,
    confirmationCode: code,
  }
  const result = await amplifyConfirmSignUp(input)
  return result
}

/**
 * Auto sign in after sign up confirmation
 * Must be called after confirmSignUpCode when autoSignIn was enabled during signUp
 */
export const autoSignIn = async () => {
  const output = await amplifyAutoSignIn()
  return output
}

/**
 * Resend sign up verification code
 * @param email - User's email address
 */
export const resendSignUpCode = async (email: string) => {
  const input: ResendSignUpCodeInput = {
    username: email,
  }
  await amplifyResendSignUpCode(input)
}

/**
 * Initiate password reset flow
 * @param email - User's email address
 */
export const resetUserPassword = async (email: string) => {
  const input: ResetPasswordInput = {
    username: email,
  }
  await amplifyResetPassword(input)
}

/**
 * Confirm password reset with code and new password
 * @param email - User's email address
 * @param code - Verification code from email
 * @param newPassword - New password
 */
export const confirmPasswordReset = async (
  email: string,
  code: string,
  newPassword: string
) => {
  const input: ConfirmResetPasswordInput = {
    username: email,
    confirmationCode: code,
    newPassword,
  }
  await amplifyConfirmResetPassword(input)
}

/**
 * Sign out the current user
 */
export const logout = async () => {
  await signOut()
}

/**
 * Get the current authenticated user
 */
export const getUser = async () => {
  try {
    const user = await getCurrentUser()
    return user
  } catch (_error) {
    return null
  }
}

/**
 * Get the current auth session with tokens
 */
export const getSession = async () => {
  try {
    const session = await fetchAuthSession()
    return session
  } catch (_error) {
    return null
  }
}

/**
 * Get the current user's access token
 */
export const getAccessToken = async () => {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.accessToken?.toString() || null
  } catch (_error) {
    return null
  }
}

/**
 * Get the current user's ID token
 */
export const getIdToken = async () => {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() || null
  } catch (_error) {
    return null
  }
}

/**
 * Get the current user's attributes (email, profile, etc.)
 */
export const getUserAttributes = async () => {
  try {
    const attributes = await fetchUserAttributes()
    return attributes
  } catch (_error) {
    return null
  }
}

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async () => {
  try {
    await getCurrentUser()
    return true
  } catch (_error) {
    return false
  }
}

/**
 * Respond to a sign-in challenge (MFA code, new password, method selection).
 * Called after signInWithEmail returns a non-DONE signInStep.
 */
export const confirmSignIn = async (challengeResponse: string) => {
  return await amplifyConfirmSignIn({ challengeResponse })
}

/**
 * Register a new passkey (WebAuthn credential) for the current user.
 * Triggers the browser/platform biometric prompt.
 */
export const registerPasskey = async () => {
  return await amplifyAssociateWebAuthnCredential()
}

/**
 * List all registered passkeys for the current user.
 */
export const listPasskeys = async () => {
  return await amplifyListWebAuthnCredentials()
}

/**
 * Delete (revoke) a registered passkey by credential ID.
 * @param credentialId - The ID of the credential to delete
 */
export const deletePasskey = async (credentialId: string) => {
  await amplifyDeleteWebAuthnCredential({ credentialId })
}

/**
 * Sign in using a passkey (WebAuthn).
 * Triggers the browser/platform biometric or security key prompt.
 */
export const signInWithPasskey = async () => {
  const input: SignInInput = {
    username: "",
    options: {
      authFlowType: "USER_AUTH",
      preferredChallenge: "WEB_AUTHN",
    },
  }
  const output = await amplifySignIn(input)
  return output
}

/**
 * Mark the current device as remembered (trusted).
 * After calling this, the device will skip MFA on future logins.
 */
export const rememberDevice = async () => {
  await amplifyRememberDevice()
}

/**
 * Forget (un-trust) a device so it requires MFA again on next login.
 * @param device - Optional device to forget. If omitted, forgets the current device.
 */
export const forgetDevice = async (device?: ForgetDeviceInput["device"]) => {
  await amplifyForgetDevice(device ? { device } : undefined)
}

/**
 * Fetch all remembered (trusted) devices for the current user.
 * @returns Array of device objects with id, name, attributes, and dates
 */
export const fetchDevices = async (): Promise<FetchDevicesOutput> => {
  return await amplifyFetchDevices()
}
